import { useTranslation } from 'react-i18next';

import { memoKey } from '@entities/handover';

import styles from './Handover.module.css';

interface HandoverMemoProps {
  memo: Record<string, string[]>;
}

/** Порядок групп: сперва перед подписанием, потом после покупки (§39.1). */
const GROUPS = ['before_signing', 'after_purchase'] as const;

/**
 * Две памятки: перед подписанием и после покупки (FE-260, MVP_PRD §39.1).
 *
 * Три правила блока, и все три из §39.2:
 *
 * * **это информационный материал, а не проверка конкретного договора.** Текст
 *   одинаков для всех пользователей и ни к чьей ситуации не относится;
 * * **никакой юридической услуги.** CarViQ.DE не составляет, не проверяет и не
 *   правит договор и не даёт правовых оценок: на рынке DE это регулируемая
 *   деятельность (`Rechtsdienstleistungsgesetz`);
 * * **список «после покупки» — не обещание услуги.** Страхование, регистрация и
 *   документы остаются делом пользователя: CarViQ.DE их не выполняет и не
 *   сопровождает.
 *
 * Обязательный дисклеймер §39.2 стоит **под памяткой**, а не в подвале
 * страницы: он относится именно к ней.
 */
export function HandoverMemo({ memo }: HandoverMemoProps) {
  const { t } = useTranslation();

  return (
    <section className={styles.section} aria-labelledby="handover-memo">
      <h2 id="handover-memo" className={styles.title}>
        {t('handover.memo.title')}
      </h2>

      {GROUPS.map((group) => {
        const codes = memo[group] ?? [];
        if (codes.length === 0) return null;
        return (
          <div key={group} className={styles.memoGroup}>
            <h3 className={styles.memoTitle}>{t(`handover.memo.${group}.title`)}</h3>
            <ul className={styles.memoList}>
              {codes.map((code) => (
                <li key={code}>{t(memoKey(group, code))}</li>
              ))}
            </ul>
          </div>
        );
      })}

      {/* Дословный текст MVP_PRD §39.2 — обязателен во всех трёх локалях. */}
      <p className={styles.warning}>{t('handover.disclaimer')}</p>
    </section>
  );
}
